import * as R from "ramda";
import React, { Fragment } from "react";
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  Create,
  Edit,
  TabbedForm,
  SimpleForm,
  SaveButton,
  Toolbar,
  DisabledInput,
  TextInput,
  SelectInput,
  DateInput,
  SearchInput,
  Filter,
  ReferenceInput,
  BooleanInput,
  ReferenceManyField,
  required,
  minLength,
  Pagination,
  FunctionField,
  ArrayField,
  SingleFieldList,
  ChipField,
  NumberField,
  DateField,
  FormTab,
  BooleanField,
  ShowButton,
} from "react-admin";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import {
  Poster,
  AvatarFileInput,
  DoctorReferenceField,
  PatientReferenceField, DeviceFamilyField,
} from "../../misc/customFields";
import { OkCancelToolbar } from "../../misc/customComponents";
import SendPushButton from "./SendPushButton";
import { PatientFullNameField } from "../../misc/customFields";
import ConsumerAside from "./ConsumerAside";
import {
  LinkPatientToConsumerButton,
  UnlinkPatientFromConsumerButton,
} from "./RowActions";
import DeleteManyConfirmButton from "./DeleteManyConfirmButton";
import DeleteConfirmButton from "./DeleteConfimButton";

const SmartShowButton = (props) => {
  if (props.record.status === "missed") {
    const iconOff = <VisibilityOff />;
    return <ShowButton icon={iconOff} basePath="#" disabled={true} />;
  }
  return <ShowButton {...props} basePath="/completed_sessions" />;
};

const PatientFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <BooleanInput source="has_token" label="Linked To PowerDot Consumer" />
  </Filter>
);

const PatientBulkActionButtons = (props) => (
  <Fragment>
    <DeleteManyConfirmButton {...props} />
  </Fragment>
);

class ToolbarEditPatient extends React.Component {
  render() {
    return (
      <Toolbar
        {...this.props}
        style={{ flex: 1, display: "flex", justifyContent: "space-between" }}
      >
        <SaveButton redirect="list" submitOnEnter={true} />
        <DeleteConfirmButton
          record={this.props.record}
          resource={this.props.resource}
        />
      </Toolbar>
    );
  }
}

export const PatientList = (props) => {
  return (
    <div>
      <List
        {...props}
        filters={<PatientFilter />}
        bulkActionButtons={<PatientBulkActionButtons />}
      >
        <Datagrid {...props}>
          <TextField source="id" />
          <PatientReferenceField label="Patient" sortBy="fl_index" />
          <EmailField source="email" sortable={false} />
          <TextField source="gender" sortable={false} />
          <TextField source="phone_number" sortable={false} />
          <DoctorReferenceField label="Clinician" sortable={false} />
          <DateField source="created" label="Data joined" />
          <LinkPatientToConsumerButton />
          <UnlinkPatientFromConsumerButton />
          <SendPushButton disabled={false} parentProps={props} />
        </Datagrid>
      </List>
    </div>
  );
};

export const PatientEdit = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm toolbar={<OkCancelToolbar />}>
        <DisabledInput label="Id" source="id" />
        <Poster />
        <TextInput source="first_name" validate={required()} />
        <TextInput source="last_name" validate={required()} />
        <TextInput source="email" type="email" validate={required()} />
        <DateInput source="date_of_birth" validate={required()} />
        <TextInput
          source="phone_number"
          validate={[required(), minLength(6)]}
        />
        <ReferenceInput
          label="Clinician"
          source="doctor"
          reference="doctors"
          validate={required()}
        >
          <SelectInput optionText="email" />
        </ReferenceInput>
        <SelectInput
          source="gender"
          choices={[
            { id: null, name: "" },
            { id: "male", name: "male" },
            { id: "female", name: "female" },
          ]}
        />
        <AvatarFileInput />
        <TextInput source="apns_token" />
      </SimpleForm>
    </Edit>
  );
};

const PatientFormTitle = ({ record }) => (
  <PatientFullNameField record={record} />
);

export class TabledPatientEdit extends React.Component {
  render() {
    const { props } = this;
    return (
      <Edit title={<PatientFormTitle />} {...props} aside={<ConsumerAside />}>
        <TabbedForm toolbar={<ToolbarEditPatient />}>
          <FormTab label="Patient">
            <DisabledInput label="Id" source="id" />
            <DateField label="Date joined" source="created" />
            <Poster />
            <TextInput source="first_name" validate={required()} />
            <TextInput source="last_name" validate={required()} />
            <TextInput source="email" type="email" validate={required()} />
            <DateInput source="date_of_birth" validate={required()} />
            <TextInput
              source="phone_number"
              validate={[required(), minLength(6)]}
            />
            <ReferenceInput
              label="Clinician"
              source="doctor"
              reference="doctors"
              validate={required()}
            >
              <SelectInput optionText="email" />
            </ReferenceInput>
            <SelectInput
              source="gender"
              choices={[
                { id: null, name: "" },
                { id: "male", name: "male" },
                { id: "female", name: "female" },
              ]}
            />
            <AvatarFileInput />
            <ReferenceInput
              label="Shared with"
              source="tenant"
              reference="doctors"
              allowEmpty
              disabled
            >
              <SelectInput optionText="email" />
            </ReferenceInput>
            <ReferenceInput
              label="Transferred From"
              source="old_owner"
              reference="doctors"
              allowEmpty
              disabled
            >
              <SelectInput optionText="email" />
            </ReferenceInput>
            <DisabledInput source="apns_token" />
            <DisabledInput source="fcm_token" />
            <DisabledInput source="patient_token" />
          </FormTab>
          <FormTab label="Completed Sessions">
            <ReferenceManyField
              addLabel={false}
              sort={{ field: "id", order: "DESC" }}
              reference="finished_sessions"
              target="patient"
              pagination={<Pagination />}
            >
              <Datagrid {...props}>
                <TextField source="id" />
                <ShowButton basePath="/completed_sessions" />
                <TextField
                  source="platformInfo.os"
                  label="OS"
                  sortBy="platformInfo__os"
                />
                <FunctionField
                  label="Completeness"
                  render={(record) =>
                    `${R.pathOr(
                      "None",
                      ["completionSummary", "completionPercent"],
                      record
                    )} %`
                  }
                />
                <TextField source="mode" sortable={false} />
                <FunctionField
                  label="Devices Number"
                  render={(record) => R.length(record.devices)}
                />
                <ArrayField source="devices" label="Serials">
                  <SingleFieldList>
                    <ChipField source="serialNumber" />
                  </SingleFieldList>
                </ArrayField>
                <TextField
                  source="programName"
                  label="Program"
                  sortBy="program__defaultParameters__type"
                />
                <TextField source="muscleGroupName" sortBy="muscleGroup" />
                <TextField source="type" sortable={false} />
                <BooleanField
                  source="is_remote"
                  sortable={false}
                  label="Remote"
                />
                <TextField
                  source="completionSummary.intensitySummary.max"
                  label="Max Intensity"
                  sortable={false}
                />
                <TextField
                  source="completionSummary.intensitySummary.longest"
                  label="Longest Intensity"
                  sortable={false}
                />
                <NumberField
                  source="painScoreBefore"
                  label="Pain Score Before"
                  sortable={false}
                />
                <NumberField
                  source="completionSummary.painScoreAfter"
                  label="Pain Score After"
                  sortable={false}
                />
                <DateField source="startDate" showTime />
                <DateField
                  source="completionSummary.finishDate"
                  showTime
                  label="End Date"
                />
              </Datagrid>
            </ReferenceManyField>
          </FormTab>
          <FormTab label="Unfinished Sessions">
            <ReferenceManyField
              addLabel={false}
              sort={{ field: "id", order: "DESC" }}
              reference="unfinished_sessions"
              target="patient"
              pagination={<Pagination />}
            >
              <Datagrid {...props}>
                <TextField source="id" />
                <ShowButton basePath="/completed_sessions" />
                <DeviceFamilyField label="Device Family" />
                <TextField
                  source="platformInfo.os"
                  label="OS"
                  sortBy="platformInfo__os"
                />
                <FunctionField
                  label="Completeness"
                  render={(record) =>
                    `${R.pathOr(
                      "None",
                      ["completionSummary", "completionPercent"],
                      record
                    )} %`
                  }
                />
                <TextField source="mode" sortable={false} />
                <FunctionField
                  label="Devices Number"
                  render={(record) => R.length(record.devices)}
                />
                <ArrayField source="devices" label="Serials">
                  <SingleFieldList>
                    <ChipField source="serialNumber" />
                  </SingleFieldList>
                </ArrayField>
                <TextField
                  source="programName"
                  label="Program"
                  sortable={false}
                />
                <TextField source="muscleGroupName" sortBy="muscleGroup" />
                <TextField source="type" sortable={false} />
                <BooleanField
                  source="is_remote"
                  sortable={false}
                  label="Remote"
                />
                <TextField
                  source="completionSummary.intensitySummary.max"
                  label="Max Intensity"
                  sortable={false}
                />
                <TextField
                  source="completionSummary.intensitySummary.longest"
                  label="Longest Intensity"
                  sortable={false}
                />
                <NumberField
                  source="painScoreBefore"
                  label="Pain Score Before"
                  sortable={false}
                />
                <NumberField
                  source="completionSummary.painScoreAfter"
                  label="Pain Score After"
                  sortable={false}
                />
                <DateField source="startDate" showTime />
                <DateField
                  source="completionSummary.finishDate"
                  showTime
                  label="End Date"
                />
              </Datagrid>
            </ReferenceManyField>
          </FormTab>
          <FormTab label="Remote Sessions">
            <ReferenceManyField
              addLabel={false}
              sort={{ field: "id", order: "DESC" }}
              reference="remote_sessions"
              target="patient"
              pagination={<Pagination />}
            >
              <Datagrid {...props}>
                <TextField source="id" />
                <SmartShowButton />
                <DoctorReferenceField sortBy="doctor__first_name" />
                <DeviceFamilyField label="Device Family" />
                <TextField
                  source="platformInfo.os"
                  label="OS"
                  sortBy="platformInfo__os"
                />
                <FunctionField
                  label="Completeness"
                  render={(record) =>
                    `${R.pathOr(
                      "None",
                      ["completionSummary", "completionPercent"],
                      record
                    )} %`
                  }
                />
                <TextField source="mode" sortable={false} />
                <FunctionField
                  label="Devices Number"
                  render={(record) => R.length(record.devices)}
                />
                <ArrayField source="devices" label="Serials">
                  <SingleFieldList>
                    <ChipField source="serialNumber" />
                  </SingleFieldList>
                </ArrayField>
                <TextField
                  source="programName"
                  label="Program"
                  sortBy="program__defaultParameters__type"
                />
                <TextField
                  source="muscleGroupName"
                  label="Muscle Group"
                  sortable={false}
                />
                <TextField source="type" sortable={false} />
                <TextField
                  source="completionSummary.intensitySummary.max"
                  label="Max Intensity"
                  sortable={false}
                />
                <TextField
                  source="completionSummary.intensitySummary.longest"
                  label="Longest Intensity"
                  sortable={false}
                />
                <NumberField
                  source="painScoreBefore"
                  label="Pain Score Before"
                  sortable={false}
                />
                <NumberField
                  source="completionSummary.painScoreAfter"
                  label="Pain Score After"
                  sortable={false}
                />
                <DateField source="startDate" showTime />
                <DateField
                  source="expectedFinishDate"
                  label="End Date (expected)"
                />
              </Datagrid>
            </ReferenceManyField>
          </FormTab>
          <FormTab label="Missed Sessions">
            <ReferenceManyField
              addLabel={false}
              sort={{ field: "id", order: "DESC" }}
              reference="missed_sessions"
              target="patient"
              pagination={<Pagination />}
            >
              <Datagrid>
                <TextField source="id" />
                <DoctorReferenceField sortBy="doctor__first_name" />
                <DeviceFamilyField label="Device Family" />
                <TextField
                  source="programName"
                  label="Program"
                  sortable={false}
                />
                <TextField
                  source="muscleGroupName"
                  label="Muscle Group"
                  sortable={false}
                />
                <TextField source="type" sortable={false} />
                <DateField source="startDate" showTime />
                <DateField
                  source="expectedFinishDate"
                  showTime
                  label="End Date (expected)"
                />
              </Datagrid>
            </ReferenceManyField>
          </FormTab>
          <FormTab label="All Sessions">
            <ReferenceManyField
              addLabel={false}
              sort={{ field: "id", order: "DESC" }}
              reference="all_sessions"
              target="patient"
              pagination={<Pagination />}
            >
              <Datagrid {...props}>
                <TextField source="id" />
                <SmartShowButton />
                <DoctorReferenceField
                  sortBy="doctor__first_name"
                  label="Clinician"
                />
                <DeviceFamilyField label="Device Family" />
                <TextField
                  source="programName"
                  label="Program"
                  sortable={false}
                />
                <TextField
                  source="muscleGroupName"
                  label="Muscle Group"
                  sortable={false}
                />
                <TextField
                  source="platformInfo.os"
                  label="OS"
                  sortBy="platformInfo__os"
                />
                <FunctionField
                  label="Completeness"
                  render={(record) =>
                    `${R.pathOr(
                      "None",
                      ["completionSummary", "completionPercent"],
                      record
                    )} %`
                  }
                />
                <TextField source="mode" sortable={false} />
                <FunctionField
                  label="Devices Number"
                  render={(record) => R.length(record.devices)}
                />
                <TextField source="type" sortable={false} />
                <BooleanField
                  source="is_remote"
                  sortable={false}
                  label="Remote"
                />
                <TextField
                  source="completionSummary.intensitySummary.max"
                  label="Max Intensity"
                  sortable={false}
                />
                <TextField
                  source="completionSummary.intensitySummary.longest"
                  label="Longest Intensity"
                  sortable={false}
                />
                <NumberField
                  source="painScoreBefore"
                  label="Pain Score Before"
                  sortable={false}
                />
                <NumberField
                  source="completionSummary.painScoreAfter"
                  label="Pain Score After"
                  sortable={false}
                />
                <DateField source="startDate" showTime />
                <DateField
                  source="completionSummary.finishDate"
                  showTime
                  label="End Date"
                />
                <DateField
                  source="expectedFinishDate"
                  showTime
                  label="End Date (expected)"
                />
              </Datagrid>
            </ReferenceManyField>
          </FormTab>
        </TabbedForm>
      </Edit>
    );
  }
}

export const PatientCreate = (props) => {
  return (
    <Create {...props}>
      <SimpleForm toolbar={<OkCancelToolbar />}>
        <TextInput source="first_name" validate={required()} />
        <TextInput source="last_name" validate={required()} />
        <TextInput source="email" type="email" validate={required()} />
        <DateInput source="date_of_birth" validate={required()} />
        <TextInput
          source="phone_number"
          validate={[required(), minLength(6)]}
        />
        <ReferenceInput
          label="Doctor"
          source="doctor_create"
          reference="doctors"
          validate={required()}
        >
          <SelectInput optionText="email" />
        </ReferenceInput>
        <SelectInput
          source="gender"
          choices={[
            { id: null, name: "" },
            { id: "male", name: "male" },
            { id: "female", name: "female" },
          ]}
        />
        <AvatarFileInput />
      </SimpleForm>
    </Create>
  );
};
